import { useMemo } from "react";
import { aiReportedNoValue } from "@platform/btx-plan-designer";
import {
  buildSbsCellStateMap,
  type SbsCellStatusEntry,
  type SbsCellStateMap,
} from "@platform/mpx-quotes-grid";
import {
  usePlanBuilderData,
  useBenefitPlanFields,
  type AiFieldLookup,
} from "@platform/document-library";
import { useProposalDocumentsQuery } from "@platform/propelx-documents";

import type { CoverageTypeEnum } from "@platform/types";

/**
 * Fetches AI Dox extraction data for the selected coverage and transforms it
 * into a {@link SbsCellStateMap} for {@link SbsCellStateProvider}.
 *
 * Each extracted field becomes an `ai_extracted` cell status, keyed by
 * benefitKey (rowKey) and planId (fieldKey). A field the AI reported as
 * not-found (empty / NOT_FOUND value) additionally carries `valueState: "null"`
 * so the cell renders the "No Data Found" ([ ]) state rather than a bare "-".
 * Mapping errors (a real AI value whose cell is empty) are derived at render
 * time by SbsCellRenderer, which has the cell value.
 */
export function useAiCellStateMap(
  ecoRfpId: string,
  selectedCoverageType: CoverageTypeEnum | undefined,
  // Source document ids of the BTX origin=document proposals — one per uploaded document,
  // i.e. every SBS document column. The ai-dox /proposals list (below) is latest-only for the
  // History tab, so these keep ALL document columns annotated, including older re-uploads.
  btxDocumentIds: string[] = [],
): {
  cellStateMap: SbsCellStateMap;
  fieldLookup: AiFieldLookup;
  isLoading: boolean;
} {
  const { coverageGroups, isLoading: isLoadingPlanBuilder } =
    usePlanBuilderData(ecoRfpId, { enabled: !!ecoRfpId });

  // Carrier proposal documents are excluded from plan-builder data (it filters
  // out proposal-typed docs), so fetch them separately and merge their ids.
  // We can't pre-filter them by coverage here, but field matching is keyed by
  // proposal plan id, so only the selected coverage's plan columns ever match.
  const { proposals } = useProposalDocumentsQuery(ecoRfpId);

  const documentIds = useMemo(() => {
    const proposalDocIds = proposals.flatMap((p) =>
      p.documents.map((d) => d.id),
    );
    const group = selectedCoverageType
      ? coverageGroups.find((g) => g.name === selectedCoverageType)
      : undefined;
    const benefitDocIds = group?.documents.map((d) => d.id) ?? [];
    // The per-document fields endpoint serves any document id (only the list endpoint is
    // latest-only), so including every document column's id annotates all columns.
    return [
      ...new Set([...benefitDocIds, ...proposalDocIds, ...btxDocumentIds]),
    ];
  }, [coverageGroups, selectedCoverageType, proposals, btxDocumentIds]);

  const documentDownloadUrls = useMemo(() => {
    const urls: Record<string, string> = {};

    proposals.forEach((proposal) => {
      proposal.documents.forEach((document) => {
        if (document.downloadUrl) {
          urls[document.id] = document.downloadUrl;
        }
      });
    });

    coverageGroups.forEach((group) => {
      group.documents.forEach((document) => {
        const url = document.metadata?.downloadUrl;
        if (url) {
          urls[document.id] = url;
        }
      });
    });

    return Object.keys(urls).length > 0 ? urls : undefined;
  }, [proposals, coverageGroups]);
  const { fieldLookup, isLoading: isLoadingFields } = useBenefitPlanFields(
    documentIds,
    {
      enabled: documentIds.length > 0,
      documentDownloadUrls,
    },
  );

  const cellStateMap = useMemo(() => {
    // lookupKey format: btxIdentifier::btxFieldName[::network]
    // useBenefitPlanFields inserts both network-qualified and non-network keys
    // for the same field. Skip the network-qualified form to avoid duplicates —
    // the non-network key already covers the same cell.
    const entries: SbsCellStatusEntry[] = Object.entries(fieldLookup).flatMap(
      ([lookupKey, field]) => {
        const parts = lookupKey.split("::");
        if (parts.length !== 2) return [];

        const [planId, benefitKey] = parts;
        const entry: SbsCellStatusEntry = {
          planId,
          benefitKey,
          statuses: ["ai_extracted"],
        };
        // AI looked and found nothing → render the explicit "No Data Found"
        // (null) state, not a bare "-" (CD-1504).
        if (aiReportedNoValue(field.value)) {
          entry.valueState = "null";
        }

        return [entry];
      },
    );

    return buildSbsCellStateMap(entries);
  }, [fieldLookup]);

  return {
    cellStateMap,
    fieldLookup,
    isLoading: isLoadingPlanBuilder || isLoadingFields,
  };
}
